import { ApiClient } from '../client/ApiClient';
import { StorefrontApiClient, createStorefrontApiClient } from '@shopify/storefront-api-client';
import {
  StorefrontCart,
  cartGQLObject,
  GetProductResponse,
  GetSellerMetaobjectResponse,
  GetStoreRequest,
  GetStoreResponse,
  SetPreferredStoreResponse,
  GetProductsByVariantIdResponse,
  GetProductHandlesBySkuResponse,
} from './types';
import { filterAndMapAttributes } from '../utils/filterAndMapAttributes';
import { v4 as uuidv4 } from 'uuid';
import { OnlineProduct } from '../shopify/types/OnlineProduct';
import { ApiResponse } from '../types';

interface NewLineItem {
  variantId: string | number | undefined;
  quantity?: number;
  attributes?: Array<{ key: string; value: string }>;
}

export interface ExistingLineItem {
  lineId: string;
  quantity?: number;
  attributes?: Array<{ key: string; value: string }>;
}

export type StorefrontClientOptions<T> = {
  storeDomain: string;
  storefrontApiKey: string;
  getCartId: () => string | undefined;
  refreshCart: () => Promise<string>;
  apiVersion?: string;
} & ([T] extends [StorefrontCart] // cartTransformer is only required if a type parameter is provided
  ? { cartTransformer?: (cart: StorefrontCart) => T | undefined }
  : { cartTransformer: (cart: StorefrontCart) => T | undefined });

export class StorefrontClient<T = StorefrontCart> extends ApiClient {
  private readonly _storeDomain: string;
  private readonly _storefrontApiKey: string;
  private readonly _apiVersion: string;
  private readonly _getCartId: () => Promise<string>;
  private readonly _refreshCart: () => Promise<string>;
  private readonly _cartTransformer: (cart: StorefrontCart) => T | undefined;

  private readonly _client: StorefrontApiClient;

  constructor(options: StorefrontClientOptions<T>) {
    super();
    const { storeDomain, storefrontApiKey, apiVersion = '2024-07', getCartId, refreshCart, cartTransformer } = options;
    this._storeDomain = storeDomain;
    this._storefrontApiKey = storefrontApiKey;
    this._apiVersion = apiVersion;
    this._refreshCart = refreshCart;
    this._cartTransformer = cartTransformer ?? (c => c as unknown as T);

    this._getCartId = async () => {
      let cartId = getCartId();
      if (!cartId) {
        cartId = await this._refreshCart();
      }
      return cartId;
    };

    this._client = createStorefrontApiClient({
      storeDomain: this._storeDomain,
      apiVersion: this._apiVersion,
      publicAccessToken: this._storefrontApiKey,
      customFetchApi: fetch,
    });
  }

  private async _fetchGql<T>(query: string, variables?: Record<string, unknown>) {
    const result = await this._client.fetch(query, { variables });
    return (await result.json()) as T;
  }

  getStore = async (id: number, body: GetStoreRequest) => {
    try {
      const query = /* GraphQL */ `
        query getLocations($latitude: Float!, $longitude: Float!) {
          locations(first: 5, near: { latitude: $latitude, longitude: $longitude }, sortKey: DISTANCE) {
            nodes {
              id
              name
              storeId: metafield(namespace: "jbhifi", key: "storeId") {
                value
              }
            }
          }
        }
      `;

      const variables = {
        latitude: body.latitude,
        longitude: body.longitude,
      };
      const data = await this._fetchGql<GetStoreResponse>(query, variables);

      let store;

      if (data?.data.locations.nodes.length) {
        store = data.data.locations.nodes.find(node => node.storeId?.value === id.toString());
      }

      return store;
    } catch (error) {
      console.error(error);
    }
  };

  setPreferredStore = async (storeId: string) => {
    const cartId = await this._getCartId();

    const query = /* GraphQL */ `
        mutation cartBuyerIdentityUpdate {
          cartBuyerIdentityUpdate(
            cartId: "gid://shopify/Cart/${cartId}"
            buyerIdentity: {
              preferences: {
                delivery: {
                  pickupHandle: [
                    "${storeId}"
                  ]
                  deliveryMethod: [
                    PICK_UP
                  ]
                }
              }
            },
          )
        {
          cart {
            buyerIdentity {
              preferences {
                delivery {
                  deliveryMethod
                  pickupHandle
                }
              }
            }
          } 
          userErrors {
              field
              code
              message
          }
        }
      }
    `;

    try {
      const response = await this._fetchGql<SetPreferredStoreResponse>(query);
      return response;
    } catch (error) {
      console.error('Error updating cart attributes:', error);
      return undefined;
    }
  };

  getProductByHandle = async (handle: string) => {
    try {
      const query = /* GraphQL */ `
        query productByHandle {
          product(handle: "${handle}") {
            id
            handle
            variants(first:100) {
              nodes {
                id
                sku
                price {
                  amount 
                }
              }
            }
          }
        }
      `;

      const data = await this._fetchGql<GetProductResponse>(query);

      return data?.data?.product;
    } catch (error) {
      console.error(error);
    }
  };

  setCartAttributes = async (
    existingAttributes: Record<string, string>,
    newAttributes: Record<string, string | undefined>
  ) => {
    const query = /* GraphQL */ `
      mutation cartAttributesUpdate ($cartId: ID!, $attributes: [AttributeInput!]!) {
        cartAttributesUpdate(cartId: $cartId, attributes: $attributes) {
          cart {
            ${cartGQLObject}
          }
          userErrors {
            field
            code
            message
          }
        }
      }
    `;
    const cartId = await this._getCartId();
    const attributes = filterAndMapAttributes({ ...existingAttributes, ...newAttributes });
    const variables = {
      cartId: `gid://shopify/Cart/${cartId}`,
      attributes: attributes,
    };

    try {
      const response = await this._fetchGql<{ data: { cartAttributesUpdate: { cart: StorefrontCart } } }>(
        query,
        variables
      );
      return this._cartTransformer(response.data.cartAttributesUpdate.cart);
    } catch (error) {
      console.error('Error updating cart attributes:', error);
      return undefined;
    }
  };

  getCart = async () => {
    const cartId = await this._getCartId();
    return await this._getExistingCart(cartId);
  };

  addToCart = async (variantId: string | number, quantity = 1, attributes?: Array<{ key: string; value: string }>) => {
    const cartId = await this._getCartId();
    return await this._addToExistingCart(cartId, [{ variantId, quantity, attributes }]);
  };

  addMultipleToCart = async (lines: Array<NewLineItem>) => {
    const cartId = await this._getCartId();
    return await this._addToExistingCart(cartId, lines);
  };

  removeFromCart = async (lineIdOrIds: string | Array<string>) => {
    const cartId = await this._getCartId();
    const lineIds = Array.isArray(lineIdOrIds) ? lineIdOrIds : [lineIdOrIds];

    const query = /* GraphQL */ `
      mutation cartLinesRemove($cartId: ID!, $lineIds: [ID!]!) {
        cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
          cart {
            ${cartGQLObject}
          }
          userErrors {
            field
            message
          }
        }
      }
    `;
    const variables = {
      cartId: `gid://shopify/Cart/${cartId}`,
      lineIds: lineIds.map(id => `gid://shopify/CartLine/${id}`),
    };

    try {
      const response = await this._fetchGql<{ data: { cartLinesRemove: { cart: StorefrontCart } } }>(query, variables);
      return this._cartTransformer(response.data.cartLinesRemove.cart);
    } catch (error) {
      console.error('Error removing item from cart:', error);
      return undefined;
    }
  };

  updateCartLines = async (lines: Array<ExistingLineItem>) => {
    const cartId = await this._getCartId();

    const query = /* GraphQL */ `
      mutation cartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
        cartLinesUpdate(cartId: $cartId, lines: $lines) {
          cart {
            ${cartGQLObject}
          }
          userErrors {
            field
            message
          }
        }
      }
    `;
    const variables = {
      cartId: `gid://shopify/Cart/${cartId}`,
      lines: lines.map(line => ({
        id: `gid://shopify/CartLine/${line.lineId}`,
        quantity: line.quantity,
        attributes: line.attributes,
      })),
    };

    try {
      const response = await this._fetchGql<{ data: { cartLinesUpdate: { cart: StorefrontCart } } }>(query, variables);
      return this._cartTransformer(response.data.cartLinesUpdate.cart);
    } catch (error) {
      console.error('Error updating item in cart:', error);
      return undefined;
    }
  };

  updateCartLine = async (lineId: string, quantity?: number, attributes?: Array<{ key: string; value: string }>) => {
    return await this.updateCartLines([{ lineId, quantity, attributes }]);
  };

  private _getExistingCart = async (cartId: string) => {
    const query = /* GraphQL */ `
          query getCart($cartId: ID!) {
            cart(id: $cartId) {
              ${cartGQLObject}
            }
          }
        `;
    const variables = {
      cartId: `gid://shopify/Cart/${cartId}`,
    };

    try {
      const response = await this._fetchGql<{ data: { cart: StorefrontCart } }>(query, variables);
      return this._cartTransformer(response.data.cart);
    } catch (error) {
      console.error('Error fetching cart:', error);
      throw error;
    }
  };

  private _addToExistingCart = async (cartId: string, lines: Array<NewLineItem>) => {
    const query = /* GraphQL */ `
      mutation cartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
        cartLinesAdd(cartId: $cartId, lines: $lines) {
          cart {
            ${cartGQLObject}
          }
          userErrors {
            field
            message
          }
        }
      }
    `;

    const variables = {
      cartId: `gid://shopify/Cart/${cartId}`,
      lines: lines.map(line => ({
        attributes: [...(line.attributes || []), { key: '_shouldSplit', value: uuidv4() }],
        quantity: line.quantity,
        merchandiseId: `gid://shopify/ProductVariant/${line.variantId}`,
      })),
    };

    try {
      const response = await this._fetchGql<{ data: { cartLinesAdd: { cart: StorefrontCart } } }>(query, variables);
      return this._cartTransformer(response.data.cartLinesAdd.cart);
    } catch (error) {
      console.error('Error adding item to cart:', error);
      return undefined;
    }
  };

  getSellerMetaobjectById = async (sellerId: string) => {
    try {
      const query = /* GraphQL */ `
        query metaobjectQuery {
          metaobject(handle: { handle:"${sellerId}", type: "seller"}) {
            seller_id: field(key: "seller_id") { value }
            name: field(key: "name") { value }
            url_handle: field(key: "url_handle") { value }
            is_digital_invoice_provided: field(key: "is_digital_invoice_provided") { value }
          }
        }
      `;

      const data = await this._fetchGql<GetSellerMetaobjectResponse>(query);

      return data?.data?.metaobject;
    } catch (error) {
      console.error(error);
    }
  };

  getProductsByVariantId = async (variantIds: Array<string>): Promise<ApiResponse<GetProductsByVariantIdResponse>> => {
    const normalisedVariantIds = variantIds
      ?.filter(id => !!id)
      .map(id => (`${id}`.match(/\D/g) ? id : `gid://shopify/ProductVariant/${id}`));

    try {
      const query =
        /* GraphQL */
        `
          query getProductsByVariantId($ids: [ID!]!) {
            nodes(ids: $ids) {
              ... on ProductVariant {
                id
                product {
                  handle
                  metafield(namespace: "online_product", key: "value") {
                    value
                  }
                }
              }
            }
          }
        `;

      const result = await this._fetchGql<{
        data: {
          nodes: Array<{
            id: string;
            product: { handle: string; metafield: { value: string } };
          }>;
        };
        variables: {
          ids: Array<string>;
        };
      }>(query, { ids: normalisedVariantIds });

      const onlineProducts: Array<{ handle: string; id: string; metafields: { onlineProduct: OnlineProduct } }> = (
        result?.data?.nodes || []
      )
        .filter(item => !!item?.product?.metafield)
        .map(item => ({
          metafields: { onlineProduct: JSON.parse(item.product.metafield.value) },
          handle: item.product.handle,
          id: item.id,
        }));

      return {
        data: {
          products: normalisedVariantIds.map(v => onlineProducts.find(p => [v].includes(p.id))).filter(p => !!p),
        },
        statusCode: 200,
      };
    } catch (error) {
      console.error(error);
      return {
        data: { products: [] },
        statusCode: 500,
        error: { message: `Error fetching products from shopify with variant ids: ${error}` },
      };
    }
  };

  getProductHandlesBySku = async (skus: Array<string>): Promise<ApiResponse<GetProductHandlesBySkuResponse>> => {
    try {
      const query = /* GraphQL */ `
        query searchProducts($query: String!, $first: Int) {
          search(query: $query, first: $first, types: PRODUCT) {
            edges {
              node {
                ... on Product {
                  id
                  handle
                  variants(first: 1) {
                    edges {
                      node {
                        sku
                      }
                    }
                  }
                }
              }
            }
          }
        }
      `;

      const result = await this._fetchGql<{
        data: {
          search: {
            edges: Array<{
              node: {
                handle: string;
                id: string;
                variants: { edges: Array<{ node: { sku: string } }> };
              };
            }>;
          };
        };
      }>(query, { query: `variants.sku:${skus.join(' OR ')}`, first: skus.length });

      const onlineProducts: Array<{
        handle: string;
        id: string;
        sku: string;
      }> = (result?.data?.search.edges || [])
        .filter(item => item?.node?.variants.edges?.length)
        .map(item => ({
          handle: item.node.handle,
          id: item.node.id,
          sku: item.node.variants.edges[0].node.sku,
        }));

      return {
        data: {
          products: skus
            .map(s => onlineProducts.find(p => p.sku === s))
            .filter((p): p is NonNullable<typeof p> => p !== undefined),
        },
        statusCode: 200,
      };
    } catch (error) {
      console.error(error);
      return {
        data: { products: [] },
        statusCode: 500,
        error: { message: `Error fetching products from shopify with skus: ${error}` },
      };
    }
  };
}
